import {
  BoundingPoly,
  // DownloadingJob,
  DetectionGap,
  AudioEditSlice,
  Transcription,
  Corsight,
  UDRsPolyAsset,
  RedactionConfig,
  ShapeType,
  HighlightedOverlay,
  FilterParameters,
} from '@common-modules/mediaDetails/models';
import { SetTDORequest } from './actions';
import { PollDownloadAssetStop } from './services';
import { CLUSTER_SEGMENT_SORT_TYPE, GROUP_TYPE } from '@helpers/constants';
import { AuthState } from '@veritone/glc-redux';

import { IndividualRedactionCode } from '@redact-modules/redactionCodes/models';
import { Patch } from 'immer';
import {
  BackgroundBlurSlice,
  HistoryMetadata,
  MaxDepthArray,
} from '@common-modules/mediaDetails/store.models';

export const namespace = 'vtn-redact-web-worker';

export interface WorkerStoreSlice {
  readonly [namespace]: WorkerState;
  readonly config: Partial<Window['config']>;
  readonly auth: AuthState;
}

export interface DataFetchedForDetectionType {
  laptop: boolean;
  head: boolean;
  poim: boolean;
  licensePlate: boolean;
  notepad: boolean;
  card: boolean;
  person: boolean;
}

/**
 * Web worker main store state.
 */

export interface ClusterMapItem {
  readonly id: string;
  readonly userLabel?: string | undefined;
  readonly pinnedDate?: number | undefined;
  readonly detectionGroupIds: string[];
  readonly udrGroupIds: string[];
  readonly sortBy?: CLUSTER_SEGMENT_SORT_TYPE;
}

export interface GroupMapItem {
  readonly id: string;
  // readonly groupId: string; // this may be preferable to "id"?
  readonly clusterId?: string; // assigned cluster - not using/setting this yet
  readonly subsegmentIds: string[]; // subsegment get combine/converted to segments later in the clusterList
  readonly picUri: string;
  readonly userLabel?: string;
  readonly label: string;
  readonly type: GROUP_TYPE;
  redactionCode?: IndividualRedactionCode;
  redactionConfig?: RedactionConfig;
  shapeType?: ShapeType;
  readonly version?: number;
}
export interface SubsegmentItem {
  id: string;
  // subsegmentId: string; //  this may be preferable to "id"?
  groupId: string; // reference to parent groupId
  objectIds: string[];
  startTimeMs: number;
  stopTimeMs: number;
}
export interface WorkerState {
  downloadingJobs: Array<PollDownloadAssetStop>;

  readonly featureFlags: Record<string, boolean | number | undefined>;

  readonly detectionEngineResultsAssetId: string | null;

  readonly filterParameters: FilterParameters;

  readonly mediaDataUrl?: string;
  readonly mediaDataContainerId?: string;
  readonly lastSaveMediaDataDisplayTime?: string;
  readonly apiRoot?: string;
  readonly newSprayPaintEditId?: string | undefined;
  readonly udrAsset: UDRsPolyAsset;

  readonly voiceMaskMediaUrl?: string;

  readonly hasSavedUdrs?: boolean | undefined;

  readonly clusterMap: {
    readonly [clusterId: string]: ClusterMapItem;
  };

  readonly groupMap: {
    readonly [groupId: string]: GroupMapItem;
  };
  readonly subsegmentMap: {
    readonly [subsegmentId: string]: SubsegmentItem;
  };
  readonly objectMap: {
    readonly [objectId: string]: BoundingPoly; // | undefined;
  };
  readonly detectionGaps: {
    readonly [groupId: string]: { [gapId: string]: DetectionGap };
  };

  readonly selectedPolyGroups: {
    readonly [id: string]: boolean | undefined;
  };

  readonly transcription?: Transcription;
  readonly speakerSeparation?: Transcription;
  readonly corsight?: Corsight;

  readonly voiceMaskEdits: Array<AudioEditSlice>;
  readonly audioRedactionEdits: Array<AudioEditSlice>;
  readonly mediaDuration: number;

  readonly backgroundBlurEdits: Array<BackgroundBlurSlice>;

  readonly tdo?: SetTDORequest['tdo'];

  /**
   * The media this load is for, recorded when the load starts.
   *
   * Separate from `tdo`, which arrives on the later IN_SET_TDO and therefore
   * still names the PREVIOUS media for a moment. Anything deciding whether a
   * response still belongs to what is open has to ask this, not `tdo`.
   */
  readonly currentTdoId?: string;

  readonly dataFetchedForDetectionType: DataFetchedForDetectionType;
  readonly facesSelectedEnginedId?: string | null;

  readonly tdoIsChanged: boolean;
  readonly highlightedOverlay: HighlightedOverlay | undefined;

  readonly lastAssignedClusterId: string | undefined;
  readonly lastDetectionGroupsWithUpdatedClusterAssignment:
    | string[]
    | undefined;
  readonly lastUdrGroupsWithUpdatedClusterAssignment: string[] | undefined;

  readonly history: {
    past: MaxDepthArray<{
      patches: Patch[];
      inversePatches: Patch[];
      metadata?: HistoryMetadata;
    } | null>;
    future: Array<{
      patches: Patch[];
      inversePatches: Patch[];
      metadata?: HistoryMetadata;
    } | null>;
  };

  readonly assetCheckpoint: number;
  readonly openedAssetCheckpoint: number;
}
